import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { FilePlus, Pencil, Trash2, X } from 'lucide-react';
import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';
import type { OfferingVorlage } from '../types';
import { stammdatenVorlageEditorPath } from '../../../utils/stammdatenRoutes';
import {
  createOfferingVorlage,
  deleteOfferingVorlage,
  loadOfferingVorlagen,
  renameOfferingVorlage,
} from '../utils/vorlageApi';

export type VorlagenSectionProps = {
  selectedFolderId: string | null;
  onFoldersRefresh: () => void | Promise<void>;
  onTotalCountChange: (count: number) => void;
};

const dateFmt = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
const eurFmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

export const VorlagenSection: React.FC<VorlagenSectionProps> = ({
  selectedFolderId,
  onFoldersRefresh,
  onTotalCountChange,
}) => {
  const navigate = useNavigate();
  const { instanceId: instanceIdParam } = useParams<{ instanceId: string }>();
  const instanceId = instanceIdParam ?? 'default';
  const [vorlagen, setVorlagen] = React.useState<OfferingVorlage[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [newVorlageName, setNewVorlageName] = React.useState('');
  const [createModalError, setCreateModalError] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<OfferingVorlage | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editingName, setEditingName] = React.useState('');
  const [rowBusyId, setRowBusyId] = React.useState<string | null>(null);

  const loadVorlagen = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await loadOfferingVorlagen();
      setVorlagen(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
      setVorlagen([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadVorlagen();
  }, [loadVorlagen]);

  React.useEffect(() => {
    onTotalCountChange(vorlagen.length);
  }, [vorlagen.length, onTotalCountChange]);

  const refreshAfterMutation = React.useCallback(() => {
    void loadVorlagen();
    void onFoldersRefresh();
  }, [loadVorlagen, onFoldersRefresh]);

  const visibleVorlagen = React.useMemo(() => {
    if (!selectedFolderId) return vorlagen;
    return vorlagen.filter((v) => v.folder_id === selectedFolderId);
  }, [vorlagen, selectedFolderId]);

  const openCreateModal = () => {
    setNewVorlageName('');
    setCreateModalError(null);
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    if (creating) return;
    setCreateModalOpen(false);
    setNewVorlageName('');
    setCreateModalError(null);
  };

  React.useEffect(() => {
    if (!createModalOpen && !deleteTarget) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (createModalOpen) closeCreateModal();
      else if (deleteTarget && !rowBusyId) setDeleteTarget(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [createModalOpen, deleteTarget, creating, rowBusyId]);

  const submitCreateVorlage = async () => {
    const name = newVorlageName.trim();
    if (!name) {
      setCreateModalError('Bitte einen Namen eingeben.');
      return;
    }
    setCreating(true);
    setCreateModalError(null);
    setError(null);
    try {
      const created = await createOfferingVorlage({
        name,
        folder_id: selectedFolderId,
      });
      setCreateModalOpen(false);
      setNewVorlageName('');
      refreshAfterMutation();
      navigate(stammdatenVorlageEditorPath(instanceId, created.id));
    } catch (e) {
      setCreateModalError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setCreating(false);
    }
  };

  const saveRename = async (id: string) => {
    const name = editingName.trim();
    if (!name) {
      setError('Der Name darf nicht leer sein.');
      return;
    }
    setRowBusyId(id);
    setError(null);
    try {
      await renameOfferingVorlage(id, name);
      setEditingId(null);
      refreshAfterMutation();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setRowBusyId(null);
    }
  };

  const deleteVorlage = async () => {
    if (!deleteTarget) return;
    const v = deleteTarget;
    setRowBusyId(v.id);
    setError(null);
    try {
      await deleteOfferingVorlage(v.id);
      setDeleteTarget(null);
      refreshAfterMutation();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setRowBusyId(null);
    }
  };

  const createModal =
    createModalOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
            onClick={() => closeCreateModal()}
            role="presentation"
          >
            <div
              className="w-full max-w-md rounded-xl border border-app-border bg-app-surface p-5 shadow-xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-vorlage-title"
            >
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 id="create-vorlage-title" className="text-base font-semibold text-app-text">
                  Neue Vorlage
                </h2>
                <button
                  type="button"
                  onClick={() => closeCreateModal()}
                  disabled={creating}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted disabled:opacity-50"
                  aria-label="Schließen"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {createModalError ? (
                <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {createModalError}
                </p>
              ) : null}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (creating) return;
                  void submitCreateVorlage();
                }}
              >
                <label className="block text-xs font-medium text-app-text-muted mb-1.5" htmlFor="vorlage-name">
                  Name der Vorlage
                </label>
                <input
                  id="vorlage-name"
                  type="text"
                  value={newVorlageName}
                  onChange={(e) => setNewVorlageName(e.target.value)}
                  disabled={creating}
                  autoFocus
                  className="w-full rounded-lg border border-app-border px-3 py-2.5 text-sm outline-none focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20"
                  placeholder="z. B. Standard-Wärmepumpe"
                  autoComplete="off"
                />

                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    disabled={creating}
                    onClick={() => closeCreateModal()}
                    className="btn-pipedrive-secondary"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    disabled={creating || !newVorlageName.trim()}
                    className="inline-flex h-9 items-center gap-2 rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838] disabled:opacity-50"
                  >
                    {creating ? <LoadingSpinner /> : null}
                    Anlegen
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )
      : null;

  const deleteModal =
    deleteTarget && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
            onClick={() => !rowBusyId && setDeleteTarget(null)}
            role="presentation"
          >
            <div
              className="relative w-full max-w-md rounded-xl border border-app-border bg-app-surface p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-vorlage-title"
            >
              <button
                type="button"
                onClick={() => !rowBusyId && setDeleteTarget(null)}
                disabled={Boolean(rowBusyId)}
                className="absolute right-3 top-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted disabled:opacity-50"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="flex flex-col items-center gap-5 px-2 text-center">
                <h2 id="delete-vorlage-title" className="text-base font-semibold text-app-text">
                  Vorlage löschen
                </h2>
                <p className="text-sm leading-relaxed text-app-text-muted">
                  „{deleteTarget.name}“ wirklich löschen? Alle gespeicherten Positionen werden entfernt.
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    type="button"
                    disabled={Boolean(rowBusyId)}
                    onClick={() => setDeleteTarget(null)}
                    className="btn-pipedrive-secondary"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(rowBusyId)}
                    onClick={() => void deleteVorlage()}
                    className="app-btn-danger-outline inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-app-surface px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {rowBusyId === deleteTarget.id ? <LoadingSpinner /> : null}
                    Löschen
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="space-y-4">
      {createModal}
      {deleteModal}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-app-text">Vorlagen</h2>
          <p className="text-sm text-app-text-subtle">Wiederverwendbare Angebotsbausteine aus Materialien, Leistungen und Blöcken.</p>
        </div>
        <button
          type="button"
          disabled={creating}
          onClick={openCreateModal}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838] disabled:opacity-50"
        >
          {creating ? <LoadingSpinner /> : <FilePlus className="w-4 h-4" />}
          Neue Vorlage
        </button>
      </div>

      {error ? (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      ) : null}

      {loading ? (
        <InlineLoadingState label="Laden…" className="py-16" />
      ) : visibleVorlagen.length === 0 ? (
        <div className="rounded-xl border border-dashed border-app-border-strong bg-app-surface p-10 text-center">
          <p className="text-sm text-app-text-muted mb-4">Noch keine Vorlagen in dieser Ansicht.</p>
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-app-border text-sm font-semibold text-app-text hover:bg-app-hover"
          >
            <FilePlus className="w-4 h-4" />
            Erste Vorlage anlegen
          </button>
        </div>
      ) : (
        <ul className="divide-y divide-app-border rounded-xl border border-app-border bg-app-surface overflow-hidden">
          {visibleVorlagen.map((v) => {
            const isEditing = editingId === v.id;
            const busy = rowBusyId === v.id;
            return (
              <li key={v.id} className="flex items-stretch hover:bg-app-hover/80">
                <div className="flex-1 min-w-0 flex items-center gap-2 px-4 py-3">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editingName}
                      autoFocus
                      disabled={busy}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void saveRename(v.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      className="flex-1 min-w-0 text-sm font-semibold border border-app-border rounded-lg px-2 py-1.5"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(stammdatenVorlageEditorPath(instanceId, v.id))}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="text-sm font-semibold text-app-text truncate">{v.name}</div>
                      <div className="text-xs text-app-text-subtle mt-0.5 tabular-nums">
                        {v.position_count ?? 0} Positionen
                        {v.updated_at ? ` · ${dateFmt.format(new Date(v.updated_at))}` : ''}
                        <span className="sm:hidden"> · {eurFmt.format(v.net_total ?? 0)} netto</span>
                      </div>
                    </button>
                  )}
                </div>
                <div className="hidden sm:flex flex-col items-end justify-center px-4 shrink-0 text-right">
                  <span className="text-sm font-semibold tabular-nums text-app-text">
                    {eurFmt.format(v.net_total ?? 0)}
                  </span>
                  <span className="text-xs text-app-text-subtle">Netto</span>
                </div>
                <div className="flex items-center gap-0.5 px-2 border-l border-app-border shrink-0">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void saveRename(v.id)}
                        className="p-2 text-xs font-semibold text-emerald-700"
                      >
                        OK
                      </button>
                      <button type="button" disabled={busy} onClick={() => setEditingId(null)} className="p-2 text-app-text-subtle">
                        Abbrechen
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setEditingId(v.id);
                          setEditingName(v.name);
                        }}
                        className="p-2 rounded-lg text-app-text-subtle hover:text-app-text-muted hover:bg-app-hover"
                        aria-label="Umbenennen"
                      >
                        {busy ? <LoadingSpinner /> : <Pencil className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setDeleteTarget(v)}
                        className="p-2 rounded-lg text-app-text-subtle hover:text-red-600 hover:bg-red-50"
                        aria-label="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
